<ng-container [ngSwitch]="state">

  <!-- ── idle ─────────────────────────────────────────────────────────── -->
  <div *ngSwitchCase="'idle'" class="vp vp--idle">
    <!-- Ink tile + resting waveform. The whole tile is the button. -->
    <button type="button" class="vp-tile" (click)="canSpeak ? listen() : type()">
      <span class="vp-tile-text">
        <span class="vp-tile-title">{{ canSpeak ? 'Just say it' : 'Just type it' }}</span>
        <span class="vp-tile-sub">One sentence about your event. We'll fill in the form.</span>
      </span>
      <span class="vp-tile-mic" aria-hidden="true">
        <ng-container *ngTemplateOutlet="canSpeak ? micIcon : penIcon"></ng-container>
      </span>
      <span *ngIf="canSpeak" class="vp-wave" aria-hidden="true">
        <i *ngFor="let h of wave" [style.height.%]="h"></i>
      </span>
    </button>
    <button *ngIf="canSpeak" type="button" class="vp-link vp-link--end" (click)="type()">Type it instead</button>
  </div>

  <!-- ── listening ────────────────────────────────────────────────────── -->
  <div *ngSwitchCase="'listening'" class="vp vp--listening">
    <div class="vp-pulse" aria-hidden="true">
      <span class="vp-mic vp-mic--lg"><ng-container *ngTemplateOutlet="micIcon"></ng-container></span>
    </div>
    <p class="vp-status">
      <span class="vp-bars" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
      Listening <span class="vp-timer">{{ timerLabel }}</span>
    </p>
    <p class="vp-sr" aria-live="polite" *ngIf="secondsLeft === 10">10 seconds left</p>
    <p class="vp-hint">Try: "Sam's 40th, Saturday at 7, for about 4 hours"</p>
    <div class="vp-actions">
      <button type="button" class="vp-btn vp-btn--text" (click)="cancel()">Cancel</button>
      <button type="button" class="vp-btn vp-btn--main" (click)="finish()">Done</button>
    </div>
    <p class="vp-privacy">Your recording goes to our speech service to become text, only to fill in this form. We don't keep it.</p>
  </div>

  <!-- ── typing ───────────────────────────────────────────────────────── -->
  <div *ngSwitchCase="'typing'" class="vp vp--typing">
    <label class="vp-label" for="vpTyped">Describe your event</label>
    <textarea id="vpTyped" class="vp-textarea" rows="3" maxlength="500" [(ngModel)]="typed"
      [ngModelOptions]="{ standalone: true }"
      placeholder="Sam's 40th, Saturday at 7, for about 4 hours"></textarea>
    <div class="vp-actions">
      <button type="button" class="vp-btn vp-btn--text" (click)="reset()">Cancel</button>
      <button type="button" class="vp-btn vp-btn--main" [disabled]="!typed.trim()" (click)="submitTyped()">Fill in the form</button>
    </div>
  </div>

  <!-- ── thinking ─────────────────────────────────────────────────────── -->
  <div *ngSwitchCase="'thinking'" class="vp vp--thinking" role="status">
    <p class="vp-transcript" *ngIf="transcript">“{{ transcript }}”</p>
    <p class="vp-hint">{{ transcript ? 'Filling in the form…' : 'Turning that into text…' }}</p>
  </div>

  <!-- ── result ───────────────────────────────────────────────────────── -->
  <ng-container *ngSwitchCase="'result'">
    <div *ngIf="collapsed; else full" class="vp vp--collapsed">
      <span class="vp-collapsed-text">Filled in from what you said</span>
      <button type="button" class="vp-link" (click)="onUndo()">Undo</button>
    </div>
    <ng-template #full>
      <div class="vp vp--result" role="status">
        <p class="vp-eyebrow">
          <!-- Solar check-linear -->
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 13L9 17L19 7" />
          </svg>
          We heard
        </p>
        <p class="vp-quote">“{{ transcript }}”</p>
        <p class="vp-note" *ngIf="partialMessage">{{ partialMessage }}</p>
        <p class="vp-note" *ngFor="let n of notes">{{ n }}</p>
        <div class="vp-result-foot">
          <span class="vp-check">Check the details, then create.</span>
          <span class="vp-result-actions">
            <button type="button" class="vp-link" (click)="onUndo()">Undo</button>
            <button type="button" class="vp-btn vp-btn--secondary vp-btn--sm" (click)="again()">
              <ng-container *ngIf="canSpeak && transcript"><ng-container *ngTemplateOutlet="micIcon16"></ng-container></ng-container>
              {{ canSpeak ? 'Say it again' : 'Edit' }}
            </button>
          </span>
        </div>
      </div>
    </ng-template>
  </ng-container>

  <!-- ── error ────────────────────────────────────────────────────────── -->
  <div *ngSwitchCase="'error'" class="vp vp--error" role="alert">
    <p class="vp-error-text" [ngSwitch]="failure">
      <ng-container *ngSwitchCase="'no_speech'">We didn't hear anything. Try again closer to the mic, or type it instead.</ng-container>
      <ng-container *ngSwitchCase="'not_understood'">We couldn't turn that into event details. Try saying what it is and when it starts.</ng-container>
      <ng-container *ngSwitchCase="'mic_blocked'">The mic is blocked for this site. Allow it in your browser settings, or type it instead.</ng-container>
      <ng-container *ngSwitchCase="'offline'">Voice needs a connection. Type it instead, or fill in the form below.</ng-container>
      <ng-container *ngSwitchCase="'transcribe_failed'">We couldn't turn that into text. Try again, or type it instead.</ng-container>
      <ng-container *ngSwitchCase="'rate_limited'">That's a lot of tries. Fill in the form below, or try again in a bit.</ng-container>
      <ng-container *ngSwitchCase="'unsupported'">Voice doesn't work in this browser. Type it instead, or fill in the form below.</ng-container>
      <ng-container *ngSwitchDefault>That didn't load. Check your connection, or fill in the form below.</ng-container>
    </p>
    <div class="vp-actions">
      <button type="button" class="vp-btn vp-btn--text" (click)="reset()">Close</button>
      <button *ngIf="canSpeak" type="button" class="vp-btn vp-btn--secondary" (click)="listen(true)">Try again</button>
      <button type="button" class="vp-btn vp-btn--secondary" (click)="type()">Type it instead</button>
    </div>
  </div>
</ng-container>

<!-- Solar microphone-2-linear -->
<ng-template #micIcon>
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.5">
      <path d="M7 8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V11C17 13.7614 14.7614 16 12 16C9.23858 16 7 13.7614 7 11V8Z" />
      <path d="M13.5 8L17 8" /><path d="M13.5 11L17 11" /><path d="M7 8L9 8" /><path d="M7 11L9 11" />
      <path d="M20 10V11C20 15.4183 16.4183 19 12 19C7.58172 19 4 15.4183 4 11V10" />
      <path d="M12 19V22" />
    </g>
  </svg>
</ng-template>
<ng-template #micIcon16>
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.5">
      <path d="M7 8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V11C17 13.7614 14.7614 16 12 16C9.23858 16 7 13.7614 7 11V8Z" />
      <path d="M13.5 8L17 8" /><path d="M13.5 11L17 11" /><path d="M7 8L9 8" /><path d="M7 11L9 11" />
      <path d="M20 10V11C20 15.4183 16.4183 19 12 19C7.58172 19 4 15.4183 4 11V10" />
      <path d="M12 19V22" />
    </g>
  </svg>
</ng-template>
<!-- Solar pen-new-square-linear -->
<ng-template #penIcon>
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.5">
      <path d="M22 10.5V12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2H13.5" />
      <path d="M16.652 3.45506L17.3009 2.80624C18.3759 1.73125 20.1188 1.73125 21.1938 2.80624C22.2687 3.88124 22.2687 5.62415 21.1938 6.69914L20.5449 7.34795M16.652 3.45506C16.652 3.45506 16.7331 4.83379 17.9497 6.05032C19.1662 7.26685 20.5449 7.34795 20.5449 7.34795M16.652 3.45506L10.6872 9.41993C10.2832 9.82394 10.0812 10.0259 9.90743 10.2487C9.70249 10.5114 9.52679 10.7957 9.38344 11.0965C9.26191 11.3515 9.17157 11.6225 8.99089 12.1646L8.41242 13.9M8.41242 13.9L8.03811 15.0229C7.9492 15.2897 8.01862 15.5837 8.21744 15.7826C8.41626 15.9814 8.71035 16.0508 8.97709 15.9619L10.1 15.5876L11.8354 15.0091C12.3775 14.8284 12.6485 14.7381 12.9035 14.6166C13.2043 14.4732 13.4886 14.2975 13.7513 14.0926C13.9741 13.9188 14.1761 13.7168 14.5801 13.3128L20.5449 7.34795M10.1 15.5876L8.41242 13.9" />
    </g>
  </svg>
</ng-template>
